import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery, injectEndpoints } from '@reduxjs/toolkit/query/react';
import { schema, normalize } from 'normalizr';
import { apiSlice } from './apiSlice';

const testsAdapter = createEntityAdapter();
const testsInitialState = testsAdapter.getInitialState();


const sessionsAdapter = createEntityAdapter();
const sessionsInitialState = sessionsAdapter.getInitialState();

// const questionsAdapter = createEntityAdapter();
// const questionsInitialState = questionsAdapter.getInitialState();

// const answersAdapter = createEntityAdapter();
// const answersInitialState = answersAdapter.getInitialState();

// Define our single API slice object

export const extendedApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({

        // INFO: for users: require authenticated and guests response
        getTests: builder.query({
            query: () => {
                return {
                    url: '/tests',
                    method: 'GET',
                }
            },
            providesTags: (result = [], error, arg) => {
                return ([
                    { type: 'Test', id: 'LIST' },
                    ...result.map(({ id }) => ({ type: 'Test', id }))
                ]);
            },
            // INFO: remove transform, restrict data to TestCard info
            // transformResponse: responseData => {
            //     return testsAdapter.setAll(testsInitialState, responseData)
            // },

        }),
        // INFO: for users that purchased the test
        getTest: builder.query({
            query: (testId) => {
                return {
                    url: `/tests/${testId}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, testId) => [
                { type: 'Test', id: testId }
            ],
        }),
        // INFO: for authors
        createTest: builder.mutation({
            query: (body) => {
                return {
                    url: `/edit`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: 'Test', id: 'LIST' }
            ],
        }),
        // INFO: for authors
        getTestsByAuthorId: builder.query({
            query: (body) => {
                return {
                    url: `/edit`,
                    method: 'GET',
                    body,
                }
            },
            providesTags: (result = [], error, arg) => [
                { type: 'Test', id: 'LIST' },
                ...result.map(({ id }) => ({ type: 'Test', id }))
            ],
        }),
        // GetEditTest: provides full test overview for the author to edit
        // TODO: Implemented versioning system: pre-review and post-review/approval updates
        // to ensure that production version works while edits are being made
        getFullTest: builder.query({
            query: (testId) => {
                return {
                    url: `/edit/${testId}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, testId) => [
                { type: 'Test', id: testId }
            ],
        }),
        editTest: builder.mutation({
            query: ({ testId, ...patch }) => {
                return {
                    url: `/edit/${testId}`,
                    method: 'PATCH',
                    body: patch,
                }
            },
            invalidatesTags: (result, error, { testId }) => [
                { type: 'Test', id: testId }
            ],
        }),
        // Edit Test - Reorder Questions only
        editTestReorderQuestions: builder.mutation({
            query: ({ testId, ...patch }) => {
                return {
                    url: `/edit/${testId}`,
                    method: 'PATCH',
                    body: patch,
                }
            },
            // Optimistic update of Question Order
            async onQueryStarted({ testId, ...patch }, { dispatch, queryFulfilled }) {
                const patchResult = dispatch(
                    apiSlice.util.updateQueryData('getFullTest', testId, (draft) => {

                        const { sourceIndex, destinationIndex } = patch;
                        const questions = Array.from(draft.questions);
                        const [reorderedItem] = questions.splice(sourceIndex, 1);
                        questions.splice(destinationIndex, 0, reorderedItem);

                        Object.assign(draft, { questions })
                    })
                )
                try {
                    await queryFulfilled
                } catch {
                    patchResult.undo()
                    // dispatch(apiSlice.util.invalidateTags([{ type: 'Test', id: testId }]))
                }
            },
        }),
        // INFO: for admins
        getTestsAdmin: builder.query({
            query: (searchParams) => {
                // console.log('searchParams', searchParams)
                return {
                    url: `/admin?${searchParams.toString()}`,
                    method: 'GET',
                }
            },
            providesTags: (result = [], error, arg) => [
                { type: 'Test', id: 'LIST' },
                ...result.map(({ id }) => ({ type: 'Test', id }))
            ],
        }),
        editTestAdmin: builder.mutation({
            query: ({ testId, ...patch }) => {
                return {
                    url: `/admin/${testId}`,
                    method: 'PATCH',
                    body: patch,
                }
            },
            invalidatesTags: (result, error, { testId }) => [
                { type: 'Test', id: testId }
            ],
        }),
        // TODO - allow for unregistered users to start sessions and get previews, but also cache and remember answers - maybe init locally
        createSession: builder.mutation({
            query: (body) => {
                return {
                    url: `/sessions`,
                    method: 'POST',
                    body,
                    // validateStatus: (response, result) => response.status === 200 && !result.isError, // Our tricky API always returns a 200, but sets an `isError` property when there is an error.
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: 'Session', id: 'LIST' }
            ],
        }),
        // TODO - allow purchases
        purchaseTest: builder.mutation({
            query: (body) => {
                return {
                    url: '/purchases',
                    method: 'POST',
                    body,
                    // validateStatus: (response, result) => response.status === 200 && !result.isError, // Our tricky API always returns a 200, but sets an `isError` property when there is an error.
                }
            },
            invalidatesTags: (result, error, { id }) => [
                { type: 'Test', id: id },
                { type: 'Test', id: 'LIST' },
            ],
        }),

        // INFO: Authorizer enabled, requires testId as searchParams to function
        getSessions: builder.query({
            query: (searchParams) => {
                return {
                    url: `/sessions?${searchParams}`,
                    method: 'GET',
                    // validateStatus: (response, result) => response.status === 200 && !result.isError, // Our tricky API always returns a 200, but sets an `isError` property when there is an error.
                }
            },
            providesTags: (result = [], error, arg) => [
                { type: 'Session', id: 'LIST' },
                // INFO: disabled as there are no 'ids' since transformResponse disabled
                // ...result.ids.map(({id}) => ({type: 'Session', id}))
                ...result.map(({ id }) => ({ type: 'Session', id }))
            ],
            // INFO: disable transformResponse
            // transformResponse: responseData => {
            //     return sessionsAdapter.setAll(sessionsInitialState, responseData)
            // },
        }),

        // INFO: Authorizer enabled
        getSession: builder.query({
            query: (sessionId) => {
                return {
                    url: `/sessions/${sessionId}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, args) => {
                // console.log(args);
                // console.log('provide tag', { type: 'Session', id: args });
                return [{ type: 'Session', id: args }]
            },
            transformResponse: responseData => {
                const data = { questions: responseData.questions };
                const questionSchema = new schema.Entity('questions', {}, { idAttribute: 'question_id' });
                const mySchema = { questions: [questionSchema] };
                const normalizedData = normalize(data, mySchema);

                return { ...responseData, ...normalizedData };
            },
        }),
        // TODO - likely for API only
        getQuestions: builder.query({
            query: (body) => {
                return {
                    url: `/sessions/${body.sessionId}/questions`,
                    method: 'GET',
                    body,
                }
            },
            providesTags: (result = [], error, arg) => [
                { type: 'Question', id: 'LIST' },
                ...result.map(({ question_id }) => ({ type: 'Question', id: question_id }))
            ],
        }),

        submitAnswer: builder.mutation({
            query: (body) => {
                return {
                    url: `sessions/${body.sessionId}/answers`,
                    method: 'PATCH',
                    body,
                }
            },
            // Implemented optimistic updates with backtrack
            // TODO implement offline service worker logic

            // invalidatesTags: (result, error, { sessionId }) => {
            //     // console.log('invalidate tag', { type: 'Session', id: sessionId });
            //     return [{ type: 'Session', id: sessionId }]
            // },

            async onQueryStarted(body, { dispatch, queryFulfilled }) {

                const [SUBMIT_ANSWER, MARK_FAVORITE, ELIMINATE_OPTION] = ['submit_answer', 'mark_favorite', 'eliminate_option'];


                const { action, sessionId, questionId } = body;

                switch (action) {
                    case SUBMIT_ANSWER:

                        const { optionId } = body

                        const patchResultSubmitAnswer = dispatch(
                            apiSlice.util.updateQueryData('getSession', sessionId, (draft) => {
                                draft.answers[questionId] = optionId;
                            })
                        )
                        queryFulfilled.catch(patchResultSubmitAnswer.undo)
                        break;
                    case MARK_FAVORITE:
                        const { isFavorite } = body

                        const patchResultFavorite = dispatch(
                            apiSlice.util.updateQueryData('getSession', sessionId, (draft) => {

                                if (draft?.favorites) {
                                    draft.favorites[questionId] = isFavorite;
                                }
                            })
                        )
                        queryFulfilled.catch(patchResultFavorite.undo)
                        break;

                }

                // now handled by switch statement
                // const patchResult = dispatch(
                //     apiSlice.util.updateQueryData('getSession', sessionId, (draft) => {
                //         draft.answers[questionId] = optionId;
                //     })
                // )
                // queryFulfilled.catch(patchResult.undo)
            },

        }),

        // TODO invalidates getSession, action: complete, build authentication; patch method
        // completeSession:
        editSession: builder.mutation({
            query: (body) => {
                return {
                    url: `sessions/${body.sessionId}`,
                    method: 'PATCH',
                    body,
                }
            },
            invalidatesTags: (result, error, { sessionId }) => {
                // console.log('invalidate tag', { type: 'Session', id: sessionId });
                return [{ type: 'Session', id: sessionId }]
            },
        }),

        // INFO: get Flashcard Deck - Authentication is optional
        getFlashcardDeck: builder.query({
            query: (flashcardId) => {
                return {
                    url: `/flashcards/${flashcardId}`,
                    method: 'GET',
                }
            },
            providesTags: (result, error, flashcardId) => [
                { type: 'Flashcards', id: flashcardId }
            ],
        }),
        createRating: builder.mutation({
            query: (body) => {
                return {
                    url: `/ratings`,
                    method: 'POST',
                    body,
                    // validateStatus: (response, result) => response.status === 200 && !result.isError, // Our tricky API always returns a 200, but sets an `isError` property when there is an error.
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: 'Rating', id: 'LIST' }
            ],
        }),

        getRatings: builder.query({
            query: (searchParams) => {
                // console.log('searchParams', searchParams)
                return {
                    url: `/ratings?${searchParams.toString()}`,
                    method: 'GET',
                }
            },
            providesTags: (result = [], error, arg) => [
                { type: 'Rating', id: 'LIST' },
                ...result.map(({ id }) => ({ type: 'Rating', id }))
            ],
        }),
        editRating: builder.mutation({
            query: ({ id, ...patch }) => {
                return {
                    url: `/ratings/${id}`,
                    method: 'PATCH',
                    body: patch,
                }
            },
            invalidatesTags: (result, error, { id }) => [
                { type: 'Rating', id: id }
            ],
        }),
        // TODO - implement deleteRating
        deleteRating: builder.mutation({
            query: (id) => {
                return {
                    url: `/ratings/${id}`,
                    method: 'DELETE',
                }
            },
            invalidatesTags: (result, error, id) => [
                { type: 'Rating', id },
                { type: 'Rating', id: 'LIST' },
            ],
        }),
        getRating: builder.query({
            query: ({ id }) => {
                // console.log('searchParams', searchParams)
                return {
                    url: `/ratings/${id}`,
                    method: 'GET',
                }
            },
            providesTags: (result = [], error, { id }) => [
                { type: 'Rating', id },
            ],
        }),
        // INFO: AI Assistant API
        // Get Thread_ID when initiating the Thread
        postAICreateThread: builder.mutation({
            query: ({ action, question, testCategory }) => ({
                url: '/ai/test',
                method: 'POST',
                body: {
                    action,
                    testCategory,
                    question,
                },
            }),
        }),
        // Post Message and get response
        postAISendMessage: builder.mutation({
            query: ({ action, testCategory, message, threadId }) => ({
                url: '/ai/test',
                method: 'POST',
                body: {
                    action,
                    testCategory,
                    message,
                    threadId
                },
            }),
        }),
        // Get a follow up questions / suggestions
        postAIGenerateFollowUp: builder.mutation({
            query: ({ action, messages }) => ({
                url: '/ai/test',
                method: 'POST',
                body: {
                    action,
                    messages,
                },
            }),
        }),
        // BeeBot
        // Get ThreadIds by UserId
        getThreadsByUserId: builder.query({
            query: () => {
                return {
                    url: `ai/beebot/threads/`,
                    method: 'GET',
                }
            },
            providesTags: (result = [], error, arg) => [
                { type: 'Thread', id: 'LIST' },
                ...result.map(({ id }) => ({ type: 'Thread', id }))
            ],
        }),
        // Get messages by threadId
        getMessagesByThreadId: builder.query({
            query: ({ threadId, lastEvaluatedKey }) => {
                const queryParams = new URLSearchParams();
                queryParams.append('threadId', threadId);
                if (lastEvaluatedKey) {
                    queryParams.append('lastEvaluatedKey', lastEvaluatedKey);
                }
                return `ai/beebot/messages?${queryParams.toString()}`;
            },
            providesTags: (result, error, { threadId }) =>
                result
                    ? [
                        ...result.messages.map(({ id }) => ({ type: 'Message', id })),
                        { type: 'Message', id: 'LIST', threadId },
                    ]
                    : [{ type: 'Message', id: 'LIST', threadId }],
        }),
        // Get User Profile
        getProfile: builder.query({
            query: (userId) => {
                return {
                    url: `/profile/${userId}`,
                    method: 'GET',
                }
            },
            providesTags: (result = [], error, userId) => [
                { type: 'Profile', id: userId },
                { type: 'Profile', id: 'DEFAULT' },
            ],
        }),
        // Update User Profile
        editProfile: builder.mutation({
            query: (body) => {
                // console.log('updateProfileBody', body);
                return {
                    url: `/profile/{body.userId}`,
                    method: 'PATCH',
                    body: {
                        name: body.name,
                        photo: body.photo,
                        allowEmail: body.allowEmail,
                    },
                }
            },
            invalidatesTags: (result, error, { userId }) => [
                { type: 'Profile', id: userId },
                { type: 'Profile', id: 'DEFAULT' },
            ],
        }),

        // BEEPREP: API call, Gen AI generate practice questions
        postBeePrepGenerateQuestions: builder.mutation({
            query: ({ formData, threadId, model }) => ({
                url: '/ai/practice',
                method: 'POST',
                body: {
                    formData,
                    threadId,
                    model
                },
            }),
        }),
        // BEEPREP: API call, Get practice questions from DB by threadId
        getBeePrepQuestionsByThreadId: builder.query({
            query: ({ threadId, lastEvaluatedKey }) => {
                const queryParams = new URLSearchParams();
                queryParams.append('threadId', threadId);
                if (lastEvaluatedKey) {
                    queryParams.append('lastEvaluatedKey', lastEvaluatedKey);
                }
                return `ai/practice?${queryParams.toString()}`;
            },
            providesTags: (result, error, { threadId }) =>
                result
                    ? [
                        ...result.questions.map(({ id }) => ({ type: 'BeePrep', id })),
                        { type: 'Question', id: 'LIST', threadId },
                    ]
                    : [{ type: 'Question', id: 'LIST', threadId }],
        }),
        // INFO: testApiPostFeedback API
        postFeedback: builder.mutation({
            query: (body) => {
                return {
                    url: `/feedback`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: 'Feedback', id: 'LIST' }
            ],
        }),

        // INFO: Paypal createOrder API
        createOrder: builder.mutation({
            query: (body) => {
                return {
                    url: `/orders`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, arg) => [
                { type: 'Order', id: 'LIST' }
            ],
        }),
        // INFO: Paypal capturePayment API
        capturePayment: builder.mutation({
            query: (body) => {
                return {
                    url: `/orders/${body.orderId}/capture`,
                    method: 'POST',
                    body,
                }
            },
            invalidatesTags: (result, error, { testId }) => [
                { type: 'Test', id: testId },
                { type: 'Test', id: 'LIST' },
                // update user balance with AI credits
                { type: 'Profile', id: 'DEFAULT' },
            ],
        }),
    }),
})


// Test Adaptor and memoized selector
export const selectTestsResult = extendedApiSlice.endpoints.getTests.select()

const selectTestsData = createSelector(
    selectTestsResult,
    testsResult => testsResult.data
)

export const { selectAll: selectAllTests, selectById: selectTestById } =
    testsAdapter.getSelectors(state => selectTestsData(state) ?? testsInitialState)


// Sessions Adaptor and memoized selector
export const selectSessionsResult = extendedApiSlice.endpoints.getSessions.select()

const selectSessionsData = createSelector(
    selectSessionsResult,
    sessionsResult => sessionsResult.data
)

export const { selectAll: selectAllSessions, selectById: selectSessionById } =
    sessionsAdapter.getSelectors(state => selectSessionsData(state) ?? sessionsInitialState)


// Export the auto-generated hook for the `getPosts` query endpoint
export const {
    useGetTestsQuery, useGetTestQuery, useCreateSessionMutation,
    useGetQuestionsQuery, useSubmitAnswerMutation, useGetSessionQuery,
    useGetSessionsQuery, usePurchaseTestMutation, useEditSessionMutation,
    useGetFullTestQuery, useEditTestMutation, useEditTestReorderQuestionsMutation,
    useGetTestsByAuthorIdQuery, useCreateTestMutation, useGetTestsAdminQuery,
    useEditTestAdminMutation, useCreateRatingMutation, useGetRatingsQuery,
    useEditRatingMutation, useDeleteRatingMutation, useGetRatingQuery,
    useCreateOrderMutation, useCapturePaymentMutation, useGetFlashcardDeckQuery,
    usePostAICreateThreadMutation, usePostAISendMessageMutation, usePostAIGenerateFollowUpMutation,
    useGetThreadsByUserIdQuery, useGetMessagesByThreadIdQuery, useGetProfileQuery, useEditProfileMutation,
    usePostBeePrepGenerateQuestionsMutation, useGetBeePrepQuestionsByThreadIdQuery,usePostFeedbackMutation,
} = extendedApiSlice;

// logout logic to clear the cache of the apiSlice/extendedApiSlice store
export const apiReducer = (state, action) => {
    if (action.type === 'beebot/logoutBeeBot') {
        state = undefined; // Reset the API slice state
    }
    return apiSlice.reducer(state, action);
};